import { z } from 'zod';
import { BundleStatus, Country, NumberLifecycle, NumberProvider, NumberType } from '../enums.js';
import { E164, TimestampSchema } from '../primitives.js';
/** Field-level rejection reasons from the Twilio Evaluations API. */
export const FailureReasonSchema = z.object({ field: z.string(), reason: z.string() });
/**
 * `businesses/{bizId}/bundles/{country}_{numberType}` — one regulatory bundle per
 * (country, numberType) (doc 05 §3). Built inside the business's Twilio
 * subaccount. An approved bundle backs any number of numbers of that country+type,
 * so "multiple numbers per customer" needs no extra KYC within a country/type.
 * Server-managed (rules deny client writes; owner may read).
 */
export const BundleDocSchema = z.object({
    country: Country,
    numberType: NumberType,
    /** Which vendor this bundle was built against (doc 17). Defaults to `twilio`
     * so pre-existing bundle docs (written before the provider split) still parse. */
    provider: NumberProvider.default('twilio'),
    bundleSid: z.string().optional(),
    endUserSid: z.string().optional(),
    addressSid: z.string().optional(),
    /** Twilio SupportingDocument SIDs (created + file-uploaded per doc 05 §2.5). */
    documentSids: z.array(z.string()).default([]),
    status: BundleStatus.default('draft'),
    failureReasons: z.array(FailureReasonSchema).default([]),
    submittedAt: TimestampSchema.optional(),
    approvedAt: TimestampSchema.optional(),
    updatedAt: TimestampSchema.optional(),
});
export const BundleDocWithIdSchema = BundleDocSchema.extend({ id: z.string() });
/**
 * `businesses/{bizId}/numbers/{numberId}` — one purchased (or in-flight) number.
 * The doc id is a stable idempotency key (a client/server `requestId`), NOT the
 * E.164 — so a duplicate approval webhook or a retry lands on the same doc
 * instead of buying twice (doc 05 §3, §4.8). The E.164 is a field, populated on
 * successful purchase. Server-managed.
 */
export const BusinessNumberSchema = z.object({
    /** Null while `purchasing`; the E.164 once the purchase succeeds. */
    phoneNumber: E164.nullable().default(null),
    twilioNumberSid: z.string().nullable().default(null),
    /** Telnyx phone-number id when `provider === 'telnyx'` (doc 17); needed to release. */
    telnyxNumberId: z.string().nullable().default(null),
    /** Which vendor this number was bought from (doc 17). Defaults to `twilio` so
     * pre-existing number docs (written before the provider split) still parse. */
    provider: NumberProvider.default('twilio'),
    country: Country,
    numberType: NumberType,
    /** Locality for local numbers (DE/PL); absent for national/mobile. */
    city: z.string().optional(),
    /** Owner-facing label, e.g. "Reception" or "Shop line". */
    label: z.string().default(''),
    /** Which bundle backs this number: `${country}_${numberType}`. */
    bundleKey: z.string(),
    status: NumberLifecycle.default('purchasing'),
    /** Exactly one active number is the business's primary (mirrored to the business doc). */
    isPrimary: z.boolean().default(false),
    /** Populated on failure so the owner sees why a purchase didn't complete. */
    error: z.string().nullable().default(null),
    createdAt: TimestampSchema.optional(),
    updatedAt: TimestampSchema.optional(),
});
export const BusinessNumberWithIdSchema = BusinessNumberSchema.extend({ id: z.string() });
/** Stable key for a (country, numberType) bundle — the `bundles` subcollection doc id. */
export function bundleKey(country, numberType) {
    return `${country}_${numberType}`;
}
//# sourceMappingURL=regulatory.js.map